import { z } from 'zod';
import { CURRENCIES } from '../money/currency.js';
import { DOCUMENT_STATUSES, DOCUMENT_TYPES } from '../state-machines/document.js';
/**
 * Schémas Zod partagés : la même validation s'applique côté client (formulaire)
 * et côté serveur (corps de requête). Une règle écrite deux fois finit toujours
 * par diverger.
 */
export const uuidSchema = z.string().uuid();
export const currencySchema = z.enum(Object.keys(CURRENCIES));
export const moneyAmountSchema = z
    .number()
    .int('Un montant doit être un entier dans la plus petite unité de la devise')
    .safe();
export const documentTypeSchema = z.enum(DOCUMENT_TYPES);
export const documentStatusSchema = z.enum(DOCUMENT_STATUSES);
export const rateSchema = z.number().min(0).max(100);
/** Slug de boutique — la liste noire évite toute collision avec les routes. */
export const RESERVED_SLUGS = [
    'admin',
    'api',
    'login',
    'logout',
    's',
    'assets',
    'static',
    'health',
    'www',
    'help',
    'legal',
    'invite',
    'uploads',
];
export const slugSchema = z
    .string()
    .min(3)
    .max(40)
    /**
     * ⭐ Le POINT est admis au meme titre que le tiret : « ma.boutique » est une
     * adresse naturelle, et rien dans une URL ne s'y oppose.
     *
     * ⚠️ Il reste SEPARATEUR, jamais en tete ni en queue, et jamais double : une
     * adresse finissant par un point casse certains clients de messagerie quand
     * elle est collee dans un message, et « .. » evoque un chemin relatif.
     */
    .regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/, 'Minuscules, chiffres, tirets et points uniquement')
    .refine((v) => !RESERVED_SLUGS.includes(v), {
    message: 'Ce nom est réservé par la plateforme',
});
/** Nom de schéma PostgreSQL — validé avant toute interpolation (§11.4). */
export const schemaNameSchema = z.string().regex(/^store_[0-9]{3,6}$/);
export const paginationSchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
});
/**
 * Messages en français, jamais ceux de Zod par défaut.
 *
 * « Invalid email » est correct mais inutilisable : il ne dit pas à
 * l'utilisateur ce qu'on attend de lui (§8.7).
 */
export const emailSchema = z
    .string()
    .email('Adresse e-mail invalide — exemple : nom@domaine.fr')
    .max(254, 'Adresse e-mail trop longue');
/**
 * Un visuel encodé en data-URI — photo d'article COMME portrait de client.
 *
 * ⭐ Une seule définition pour les deux. Elles vivaient séparément, et elles
 * ont divergé exactement comme l'annonce l'en-tête de ce fichier : le plafond
 * des articles est passé à 400 000 avec le nouveau format, celui des clients
 * est resté à 65 536. Les photos de clients étaient refusées — en silence,
 * puisque le moteur de synchronisation parque les mutations refusées.
 *
 * ⚠️ Le plafond suit la résolution de stockage, passée à 640×854. Un visuel
 * pèse alors ~70 Ko, soit ~95 000 caractères en base64 ; une photo très bruitée
 * — le pire cas pour JPEG — peut doubler. 400 000 caractères laissent cette
 * marge sans ouvrir la porte à l'envoi d'originaux non redimensionnés, qui
 * dépassent le million.
 *
 * ⚠️ CE PLAFOND EXISTE AUSSI EN BASE : contrainte `items_image_size` /
 * `customers_avatar_size` (migration tenant `0011_visuels_640.sql`). Les deux
 * doivent bouger ENSEMBLE. Relever celui-ci seul laisse passer la validation
 * puis heurte la contrainte à l'INSERT : la mutation part en « refusée », le
 * moteur la parque définitivement, et l'article n'existe plus que sur
 * l'appareil pendant que l'entête affiche « Synchronisé ».
 */
export const visuelSchema = z
    .string()
    .max(400_000, 'Image trop lourde — elle doit être redimensionnée avant envoi')
    .regex(/^data:image\/(jpeg|png|webp);base64,/, "Format d'image non pris en charge");
//# sourceMappingURL=common.js.map